import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarDays, Plus, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import { ScheduleCard, StatusPill, Meta, MapPin, Users, PoundSterling } from "@/components/ScheduleCard";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const todayIso = () => iso(new Date());
const shift = (s, n) => { const d = new Date(`${s}T00:00:00`); d.setDate(d.getDate() + n); return iso(d); };
const dayLabel = (s) => {
  const d = new Date(s); const t = new Date(); t.setHours(0, 0, 0, 0);
  const dd = new Date(d); dd.setHours(0, 0, 0, 0);
  const diff = Math.round((dd - t) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" });
};

function CoachCard({ c }) {
  const owed = c.awaiting_feedback || [];
  return (
    <ScheduleCard
      to={owed.length ? `/offerings/${c.id}?feedback=1` : `/offerings/${c.id}`}
      tone={c.status_tone} title={c.title} when={`${c.when} · ${c.duration_min} min`} dim={c.past && !owed.length}
      highlight={owed.length > 0} testid={`coach-schedule-card-${c.id}`}
      meta={[
        <Meta key="p" icon={Users}><span data-testid={`card-enrolled-${c.id}`}>{c.enrolled}/{c.max_spaces} players</span></Meta>,
        <Meta key="pay" icon={PoundSterling}><span data-testid={`card-paid-${c.id}`}>{c.paid}/{c.enrolled} paid</span></Meta>,
        c.venue && <Meta key="v" icon={MapPin}>{c.venue}</Meta>,
      ].filter(Boolean)}
      pills={[
        <StatusPill key="s" tone={c.status_tone} testid={`card-status-${c.id}`}>{c.status_label}</StatusPill>,
        c.unpaid > 0 && !c.past && <StatusPill key="u" tone="amber">{c.unpaid} unpaid</StatusPill>,
        owed.length > 0 && <StatusPill key="f" tone="red" testid={`card-feedback-${c.id}`}>Awaiting feedback: {owed.length} player{owed.length === 1 ? "" : "s"}</StatusPill>,
        c.is_assessment && <StatusPill key="a" tone="muted">Assessment</StatusPill>,
      ].filter(Boolean)}
    />
  );
}

function DayGroup({ day, items }) {
  return (
    <section className="space-y-3" data-testid={`schedule-day-${day}`}>
      <div className="flex items-baseline gap-3">
        <h3 className="text-sm font-bold uppercase tracking-[0.14em] text-white">{dayLabel(day)}</h3>
        <span className="text-xs text-[#525252]">{items.length} session{items.length === 1 ? "" : "s"}</span>
      </div>
      <div className="grid gap-3 md:grid-cols-2">{items.map((c) => <CoachCard key={c.id} c={c} />)}</div>
    </section>
  );
}

const groupByDay = (list) => list.reduce((m, c) => { const k = c.start.slice(0, 10); (m[k] = m[k] || []).push(c); return m; }, {});

export default function CoachSchedule() {
  const [start, setStart] = useState(todayIso());
  const [older, setOlder] = useState(false);
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/coach/schedule", { params: { start, days: 14, older } }).then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  }, [start, older]);
  const ctl = useListControls(data?.items, { name: (c) => c.title, fields: (c) => [c.title, c.location, ...(c.players || []).map((p) => p.name)], initialSort: "default" });
  const items = ctl.list;
  const inWindow = items.filter((c) => c.start.slice(0, 10) >= start);
  const recent = items.filter((c) => c.start.slice(0, 10) < start && !c.older);
  const old = items.filter((c) => c.older);
  if (ctl.sort === "default") { recent.reverse(); old.reverse(); }
  const owedTotal = (data?.items || []).reduce((n, c) => n + (c.awaiting_feedback?.length || 0), 0);
  const section = (title, list, testid, empty) => (
    <div className="space-y-6" data-testid={testid}>
      <div className="label-mono text-[#A3A3A3]">{title}</div>
      {!list.length ? <div className="text-sm text-[#525252]">{ctl.query ? "No sessions match your search." : empty}</div>
        : ctl.sort === "default" ? Object.entries(groupByDay(list)).map(([d, l]) => <DayGroup key={d} day={d} items={l} />)
          : <div className="grid gap-3 md:grid-cols-2">{list.map((c) => <CoachCard key={c.id} c={c} />)}</div>}
    </div>
  );
  return (
    <div className="max-w-5xl space-y-10" data-testid="coach-schedule">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono mb-2 text-[#CCFF00]">SCHEDULE</div>
          <h1 className="h-display text-4xl sm:text-5xl tracking-tighter">Your next 14 days.</h1>
          <p className="text-sm text-[#A3A3A3] mt-2">{dayLabel(start)} → {new Date(`${shift(start, 13)}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}{owedTotal ? ` · ${owedTotal} feedback owed` : ""}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex border border-[#262626] rounded-full overflow-hidden">
            <button type="button" onClick={() => setStart(shift(start, -14))} className="px-3 py-2 hover:bg-[#1A1A1A]" data-testid="schedule-prev" aria-label="Previous 14 days"><ChevronLeft className="w-4 h-4" /></button>
            <button type="button" onClick={() => setStart(todayIso())} className="px-4 py-2 text-xs uppercase tracking-wider font-bold hover:bg-[#1A1A1A] border-x border-[#262626]" data-testid="schedule-today">Today</button>
            <button type="button" onClick={() => setStart(shift(start, 14))} className="px-3 py-2 hover:bg-[#1A1A1A]" data-testid="schedule-next" aria-label="Next 14 days"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <Link to="/offerings/new" className="px-4 py-2 rounded-full bg-[#CCFF00] text-black text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5" data-testid="schedule-new"><Plus className="w-3.5 h-3.5" /> New session</Link>
          <Link to="/calendar/classic" className="px-3 py-2 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1.5" data-testid="schedule-classic"><CalendarDays className="w-3.5 h-3.5" /> Month view</Link>
        </div>
      </header>
      {!data ? <div className="text-sm text-[#A3A3A3] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading schedule…</div> : (
        <>
          <ListControlsBar ctl={ctl} modes={["default", "az", "za"]} placeholder="Search sessions or players…" testid="schedule" />
          {section("UPCOMING", inWindow, "schedule-upcoming", "Nothing scheduled in these 14 days.")}
          {section("PAST 14 DAYS", recent, "schedule-recent", "No sessions in the last 14 days.")}
          <label className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#A3A3A3] cursor-pointer" data-testid="schedule-show-older">
            <input type="checkbox" checked={older} onChange={(e) => setOlder(e.target.checked)} className="w-4 h-4 accent-[#CCFF00]" /> Show older sessions
          </label>
          {older && section("OLDER", old, "schedule-older", "No older sessions.")}
        </>
      )}
    </div>
  );
}
